import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

const barStyles =
    'border border-gray-300 bg-white px-3 py-2 leading-normal text-gray-500 hover:bg-gray-100 hover:text-gray-700';
const disabledButtonStyle = 'bg-gray-100';
const inputStyles =
    'w-12 border border-gray-300 px-2 py-2 leading-normal focus:outline-none focus:border-blue-500';

function Pagination({ orderAndPaginationControl: { pageCount, currentPage, setCurrentPage } }) {
    const [inputValue, setInputValue] = useState(currentPage);

    function handlePrevious() {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    }

    function handleNext() {
        if (currentPage < pageCount) {
            setCurrentPage(currentPage + 1);
        }
    }

    function handleGoToPage() {
        const pageNumber = parseInt(inputValue, 10);
        if (!Number.isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= pageCount) {
            setCurrentPage(pageNumber);
        } else {
            // Reset input to current page if invalid.
            setInputValue(currentPage);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleGoToPage();
        }
    }

    function handleInputChange(e) {
        const newValue = e.target.value;
        setInputValue(newValue);
    }

    // Update input value when currentPage changes.
    useEffect(() => {
        setInputValue(currentPage);
    }, [currentPage]);

    return (
        <div className="inline-flex items-center justify-end -space-x-px py-1">
            <button
                disabled={currentPage === 1}
                onClick={handlePrevious}
                className={`ml-0 rounded-l-lg ${barStyles} ${
                    currentPage === 1 && disabledButtonStyle
                }`}
            >
                {gettext('PREVIOUS')}
            </button>
            <div className="flex items-center">
                <span className={barStyles}>{gettext('PAGE')}</span>
                <input
                    type="number"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    className={inputStyles}
                    aria-label={gettext('PAGE_NUMBER')}
                />
                <span className={barStyles}>
                    {gettext('OF_TOTAL')} {pageCount}
                </span>
                <button
                    onClick={handleGoToPage}
                    className={barStyles}
                    disabled={!inputValue || parseInt(inputValue, 10) === currentPage}
                >
                    {gettext('GO')}
                </button>
            </div>
            <button
                disabled={currentPage === pageCount}
                onClick={handleNext}
                className={`rounded-r-lg ${barStyles} ${
                    currentPage === pageCount && disabledButtonStyle
                }`}
            >
                {gettext('NEXT')}
            </button>
        </div>
    );
}

Pagination.propTypes = {
    orderAndPaginationControl: PropTypes.object.isRequired,
};

export default Pagination;
